import { Component, OnDestroy, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { environment } from '../../../environments/environment';
import { USERS_API } from './users-list.component';
import { PhotoChange } from '../photo-editor.component';

const INSPECTOR_LEVELS = [1, 2];   // administrator and inspector share twin-ci's Inspector panel
const CREW_LEVEL = 15;
const CONTRACTOR_LEVEL = 16;

/**
 * Settings > Users > Add / Edit user (/users/add, /users/:id). The mockup has a short Add User form
 * only; every twin-ci users_add / users_edit field is kept (agreed), by level:
 * - all levels: name, email (sign-in), level, active, weekly report opt-in, password (admin sets it;
 *   blank on edit keeps it);
 * - administrator / inspector: state license, phones, treater + applicator license, crew work,
 *   commission, address, branch, photo, signature, and (edit) Google / Office 365 calendar access;
 * - crew: hourly rate, commission, phones, treater + license, address, branch, photo;
 * - contractor: contractor rate, phones, address, branch, photo.
 * Calendar connect / revoke still run in twin-ci (its OAuth callbacks), which now return here.
 */
@Component({
    selector: 'app-user-form',
    templateUrl: './user-form.component.html',
    styleUrls: ['./user-form.component.scss']
})
export class UserFormComponent implements OnInit, OnDestroy {
    id: number = null;
    form: any = null;
    levels: { user_level_id: number, user_level_name: string }[] = [];
    branches: { branch_id: number, branch_name: string }[] = [];
    states: string[] = [];
    photoUrl: string = null;
    signatureUrl: string = null;
    calendars: any = null;
    photo: File = null;
    signature: File = null;
    removePhoto = false;
    removeSignature = false;
    errors: { [field: string]: string } = {};
    error = '';
    message = '';
    saving = false;
    readonly signatureTemplate = environment.oldSite + 'media/signatures/signature_template_image.jpg';
    private subscription: Subscription;

    constructor(private http: HttpClient, private route: ActivatedRoute, private router: Router) { }

    ngOnInit() {
        this.http.get<{ id: string }[]>('assets/states.json').subscribe(list => this.states = list.map(s => s.id));
        this.http.get<any>(USERS_API + '/options').subscribe(res => {
            this.levels = res.levels || [];
            this.branches = res.branches || [];
            if (this.form && !this.form.branch_id && this.branches.length) {
                this.form.branch_id = this.branches[0].branch_id;   // twin-ci: first branch preselected
            }
        });
        this.subscription = this.route.paramMap.subscribe(params => {
            const id = params.get('id');
            this.id = id && id !== 'add' ? Number(id) : null;
            this.reset();
            if (this.id) {
                this.load();
            } else {
                this.form = this.blank();
            }
        });
    }

    ngOnDestroy() {
        this.subscription.unsubscribe();
    }

    private reset() {
        this.form = null;
        this.photo = this.signature = null;
        this.removePhoto = this.removeSignature = false;
        this.errors = {};
        this.error = this.message = '';
        this.photoUrl = this.signatureUrl = null;
        this.calendars = null;
    }

    private blank() {
        return {
            user_first_name: '', user_last_name: '', user_email: '', user_level_id: '', user_active: true, is_allow_weekly_report: false,
            user_password: '', user_password_confirmation: '',
            user_state_license: '', user_phone: '', user_cell: '', user_address1: '', user_address2: '', user_city: '', user_state: 'CA',
            user_zip: '', branch_id: this.branches.length ? this.branches[0].branch_id : '', user_is_treater: 0, user_is_crew: 0,
            user_treater_license: '', user_crew_commission: '', user_crew_rate: '', user_contractor_rate: '',
        };
    }

    private load() {
        this.http.get<any>(USERS_API + '/' + this.id).subscribe(
            res => {
                const f = this.blank();
                Object.keys(f).forEach(k => { if (res[k] !== undefined && res[k] !== null) { f[k] = res[k]; } });
                f.user_active = res.user_active == 1;
                f.is_allow_weekly_report = res.is_allow_weekly_report == 1;
                f.user_is_treater = Number(res.user_is_treater) || 0;
                f.user_is_crew = Number(res.user_is_crew) || 0;
                f.user_password = f.user_password_confirmation = '';
                if (!f.branch_id && this.branches.length) { f.branch_id = this.branches[0].branch_id; }
                this.form = f;
                this.photoUrl = res.photo_url;
                this.signatureUrl = res.signature_url;
                this.calendars = res.calendars;
            },
            err => this.error = typeof err === 'string' ? err : 'This user could not be loaded.'
        );
    }

    get level() {
        return Number(this.form && this.form.user_level_id) || 0;
    }

    get isInspector() { return INSPECTOR_LEVELS.indexOf(this.level) !== -1; }
    get isCrew() { return this.level === CREW_LEVEL; }
    get isContractor() { return this.level === CONTRACTOR_LEVEL; }
    get hasPanel() { return this.isInspector || this.isCrew || this.isContractor; }

    get panelTitle() {
        return this.isInspector ? 'Inspector Details' : (this.isCrew ? 'Crew Member Details' : 'Sub Contractor Details');
    }

    get title() {
        return this.id ? 'Edit User' : 'Add New User';
    }

    pickFile(kind: 'photo' | 'signature', input: HTMLInputElement) {
        this[kind] = input.files && input.files.length ? input.files[0] : null;
    }

    // Profile picture editor: a cropped picture to upload, or remove the current one.
    onPhoto(change: PhotoChange) {
        this.photo = change.file;
        this.removePhoto = change.remove;
    }

    private validate() {
        const f = this.form;
        const e: { [field: string]: string } = {};
        if (!String(f.user_last_name).trim()) { e.user_last_name = 'Last or Company Name is required.'; }
        if (!f.user_level_id) { e.user_level_id = 'Choose a user level.'; }
        if (!String(f.user_email).trim()) {
            e.user_email = 'Email is required.';
        } else if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(String(f.user_email).trim())) {
            e.user_email = 'Enter a valid email address.';
        }
        if (!this.id && !f.user_password) { e.user_password = 'Password is required.'; }
        if (f.user_password !== f.user_password_confirmation) { e.user_password_confirmation = 'The passwords do not match.'; }
        const rate = (field: string, label: string) => {
            const v = f[field];
            if (v !== '' && v !== null && !(isFinite(Number(v)) && Number(v) >= 0)) { e[field] = label + ' must be a number, 0 or more.'; }
        };
        if (this.isInspector || this.isCrew) {
            const c = f.user_crew_commission;
            if (c !== '' && c !== null && !(Number.isInteger(Number(c)) && Number(c) >= 0 && Number(c) <= 100)) {
                e.user_crew_commission = 'Commission must be a whole number from 0 to 100.';
            }
        }
        if (this.isCrew) { rate('user_crew_rate', 'Crew Hourly Rate'); }
        if (this.isContractor) { rate('user_contractor_rate', 'Contractor Rate'); }
        this.errors = e;
        return !Object.keys(e).length;
    }

    save() {
        this.error = this.message = '';
        if (!this.validate()) {
            this.error = 'Check the highlighted fields.';
            return;
        }
        const body = new FormData();
        Object.keys(this.form).forEach(k => {
            let v = this.form[k];
            if (v === true || v === false) { v = v ? 1 : 0; }
            body.append(k, v === null || v === undefined ? '' : String(v));
        });
        if (this.photo) { body.append('photo', this.photo); }
        if (this.signature) { body.append('signature', this.signature); }
        body.append('remove_photo', this.removePhoto ? '1' : '0');
        body.append('remove_signature', this.removeSignature ? '1' : '0');
        this.saving = true;
        this.http.post<any>(USERS_API + (this.id ? '/' + this.id : ''), body).subscribe(
            res => {
                this.saving = false;
                if (!this.id) {
                    // twin-ci showed the list after Add; open the new user so calendars can be connected
                    this.router.navigate(['/users', res.id]);
                    return;
                }
                this.message = res.message || 'User Edited';
                this.reset();
                this.load();
            },
            err => {
                this.saving = false;
                this.error = typeof err === 'string' ? err : 'The user could not be saved.';
            }
        );
    }
}
